import { CASE_TYPES, caseInputSchema, procedureInputSchema } from '@iculogbook/shared';
import type { CaseInput, CaseType, ProcedureInput } from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import { useAuth } from '../auth/AuthProvider.tsx';
import { Button, Dialog, Spinner, useToast } from '../components/index.ts';
import { casesRepository } from '../db/repositories/cases.ts';
import { proceduresRepository } from '../db/repositories/procedures.ts';
import { CaseForm } from '../features/log/CaseForm.tsx';
import { NestedProcedureSheet } from '../features/log/NestedProcedureSheet.tsx';
import type { FormErrors } from '../features/log/ProcedureForm.tsx';
import { uuidv7 } from 'uuidv7';

import {
  INHERITED_FIELDS,
  applyCaseValuesToDrafts,
  buildCaseInput,
  caseValuesFrom,
  inheritedChanges,
  initialCaseValues,
  markOverridden,
  nestedDraftFrom,
  nestedProcedureDraft,
  procedureChanges,
} from '../features/log/caseForm.ts';
import type { CaseFormValues, NestedProcedureDraft } from '../features/log/caseForm.ts';
import { buildProcedureInput, procedureValuesFrom } from '../features/log/procedureForm.ts';

type DuplicatedCase = { case: CaseInput; procedures: ProcedureInput[] };

function caseTypeFrom(value: string | null): CaseType {
  return CASE_TYPES.find((type) => type === value) ?? 'admission';
}

/**
 * Logging a case, and editing one afterwards.
 *
 * A case is one clinical event, so it and the procedures recorded inside it are
 * written in a single transaction — either the whole admission lands or none of
 * it does. Everything is local; the network is not on this path.
 */
function LogCaseRouteForm() {
  const { user } = useAuth();
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const userId = user?.id ?? null;
  const editing = caseId !== undefined;

  const [values, setValues] = useState<CaseFormValues | null>(null);
  const [procedures, setProcedures] = useState<readonly NestedProcedureDraft[]>([]);
  const [removedIds, setRemovedIds] = useState<readonly string[]>([]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [inheritOffer, setInheritOffer] = useState<CaseFormValues | null>(null);
  /** Whether the save waiting on the inheritance offer should then duplicate. */
  const [duplicateAfter, setDuplicateAfter] = useState(false);

  /** The stored case and its procedures, when editing. Undefined while loading. */
  const loaded = useLiveQuery(async () => {
    if (userId === null || caseId === undefined) return null;

    const record = await casesRepository.get(userId, caseId);
    if (record === undefined) return null;

    return { record, children: await proceduresRepository.listByCase(userId, caseId) };
  }, [userId, caseId]);

  const recentKeys =
    useLiveQuery(
      async () => (userId === null ? [] : await proceduresRepository.recentlyUsedKeys(userId)),
      [userId],
    ) ?? [];

  /*
   * Seed the form once: from the stored case when editing, otherwise from the
   * case type the chooser passed in. After that the form owns its state — a
   * live query must never overwrite something half-typed.
   */
  const [seeded, setSeeded] = useState(false);
  if (!seeded && values === null) {
    if (!editing) {
      setSeeded(true);

      /*
       * A duplicate arrives prefilled through the router, never through the
       * URL — a copied case carries free text, and free text does not belong
       * in a browser history entry.
       */
      const prefill = (location.state as { prefill?: DuplicatedCase } | null)?.prefill;

      if (prefill === undefined) {
        setValues(initialCaseValues(caseTypeFrom(searchParams.get('type')), new Date()));
      } else {
        setValues(caseValuesFrom(prefill.case));
        setProcedures(
          prefill.procedures.map((procedure) => ({
            draftId: uuidv7(),
            saved: false,
            values: procedureValuesFrom(procedure),
            // A copy keeps the shape of the original event, so each procedure
            // holds the time it had rather than snapping to the case's.
            overridden: [...INHERITED_FIELDS],
          })),
        );
      }
    } else if (loaded !== undefined && loaded !== null) {
      setSeeded(true);
      setValues(caseValuesFrom(loaded.record));
      setProcedures(loaded.children.map(nestedDraftFrom));
    }
  }

  const stored = loaded === undefined || loaded === null ? null : loaded.record;
  const editingDraft = procedures.find((draft) => draft.draftId === editingDraftId) ?? null;

  function changeCaseValues(next: CaseFormValues): void {
    setValues(next);
    // Unsaved procedures follow the case unless the user has overridden the
    // field themselves. Stored ones never move without the offer below.
    setProcedures((current) => applyCaseValuesToDrafts(current, next));
  }

  function addProcedure(): void {
    setEditingDraftId(null);
    setSheetOpen(true);
  }

  function selectProcedure(procedureKey: string): void {
    if (values === null) return;

    const draft = nestedProcedureDraft(values, procedureKey);
    setProcedures((current) => [...current, draft]);
    setEditingDraftId(draft.draftId);
  }

  function removeProcedure(): void {
    const draft = editingDraft;
    if (draft === null) return;

    if (draft.saved) setRemovedIds((current) => [...current, draft.draftId]);
    setProcedures((current) => current.filter((entry) => entry.draftId !== draft.draftId));
    setEditingDraftId(null);
    setSheetOpen(false);
  }

  async function save(applyToStoredProcedures = false, andDuplicate = false): Promise<void> {
    if (values === null || userId === null) return;

    const input = buildCaseInput(values);
    if (input === null) {
      setErrors({
        ...(values.title.trim() === '' ? { title: 'Give the case a short title.' } : {}),
        ...(values.ageBand === null ? { ageBand: 'Choose an age band.' } : {}),
        ...(values.capabilityLevel === null
          ? { capabilityLevel: 'Choose a capability level.' }
          : {}),
      });
      return;
    }

    const parsedCase = caseInputSchema.safeParse(input);
    if (!parsedCase.success) {
      setErrors(collect(parsedCase.error.issues));
      return;
    }

    // Every nested procedure has to be complete too. Saving a case while one of
    // its procedures is missing an age band would either drop the procedure or
    // write an invalid record; both are worse than saying so.
    const built: ProcedureInput[] = [];
    for (const draft of procedures) {
      const procedureInput = buildProcedureInput(draft.values);
      const parsed =
        procedureInput === null ? null : procedureInputSchema.safeParse(procedureInput);

      if (parsed === null || !parsed.success) {
        setErrors({ procedures: 'One of the procedures in this case is not complete yet.' });
        setEditingDraftId(draft.draftId);
        setSheetOpen(true);
        return;
      }

      built.push(parsed.data);
    }

    setSaving(true);

    try {
      let savedId = caseId;

      if (!editing || caseId === undefined) {
        const created = await casesRepository.createWithProcedures(userId, parsedCase.data, built);
        savedId = created.case.id;
      } else {
        const added = procedures
          .map((draft, index) => ({ draft, input: built[index] }))
          .filter((entry) => !entry.draft.saved && entry.input !== undefined)
          .map((entry) => entry.input as ProcedureInput);

        /*
         * Stored procedures send only what the user changed on them. A whole
         * record would out-rank the case's inherited values below and quietly
         * undo an "apply to all".
         */
        const updated = procedures
          .map((draft, index) => ({ draft, input: built[index] }))
          .filter((entry) => entry.draft.saved && entry.input !== undefined)
          .map((entry) => {
            const original = loaded?.children.find((child) => child.id === entry.draft.draftId);
            const before =
              original === undefined ? null : buildProcedureInput(nestedDraftFrom(original).values);

            return {
              id: entry.draft.draftId,
              changes:
                before === null
                  ? (entry.input as ProcedureInput)
                  : procedureChanges(before, entry.input as ProcedureInput),
            };
          })
          .filter((entry) => Object.keys(entry.changes).length > 0);

        await casesRepository.updateWithProcedures(userId, caseId, parsedCase.data, {
          added,
          updated,
          removedIds,
          ...(applyToStoredProcedures && stored !== null
            ? { inherited: inheritedChanges(caseValuesFrom(stored), values) }
            : {}),
        });
      }

      showToast({
        message: editing
          ? 'Case updated.'
          : `Case saved${built.length === 0 ? '' : ` with ${String(built.length)} procedure${built.length === 1 ? '' : 's'}`}.`,
        tone: 'success',
      });

      const copy =
        andDuplicate && savedId !== undefined
          ? await casesRepository.duplicateWithProcedures(userId, savedId)
          : undefined;

      void navigate(copy === undefined ? '/entries' : '/log/case', {
        ...(copy === undefined ? {} : { state: { prefill: copy } }),
      });
    } finally {
      setSaving(false);
    }
  }

  /**
   * Saving an edit that moved the case's date, age band or capability level
   * asks, once, whether the procedures inside it should move too. It is never
   * applied silently — see DATA_MODEL.md.
   */
  function saveWithInheritanceCheck(andDuplicate = false): void {
    if (values === null) {
      void save(false, andDuplicate);
      return;
    }

    const storedChildren = procedures.filter((draft) => draft.saved).length;
    const changed =
      editing && stored !== null ? inheritedChanges(caseValuesFrom(stored), values) : {};

    if (storedChildren > 0 && Object.keys(changed).length > 0) {
      setInheritOffer(values);
      setDuplicateAfter(andDuplicate);
      return;
    }

    void save(false, andDuplicate);
  }

  async function deleteCase(): Promise<void> {
    if (userId === null || caseId === undefined) return;

    const removed = await casesRepository.softDelete(userId, caseId);
    setConfirmDelete(false);

    showToast({
      message:
        removed.length === 0
          ? 'Case deleted.'
          : `Case and ${String(removed.length)} procedure${removed.length === 1 ? '' : 's'} deleted.`,
      actions: [
        {
          label: 'Undo',
          onAction: () => {
            void casesRepository.restoreWithProcedures(userId, caseId, removed);
            showToast({ message: 'Case restored.' });
          },
        },
      ],
    });

    void navigate('/entries');
  }

  if (values === null) {
    return editing && loaded === null ? (
      <p className="text-sm text-muted">That case no longer exists.</p>
    ) : (
      <Spinner label="Loading" />
    );
  }

  const storedChildCount = procedures.filter((draft) => draft.saved).length;

  return (
    <>
      <CaseForm
        values={values}
        onChange={changeCaseValues}
        procedures={procedures}
        onAddProcedure={addProcedure}
        onEditProcedure={(draftId) => {
          setEditingDraftId(draftId);
          setSheetOpen(true);
        }}
        onSave={() => saveWithInheritanceCheck()}
        onSaveAndDuplicate={() => saveWithInheritanceCheck(true)}
        onDelete={editing ? () => setConfirmDelete(true) : undefined}
        saving={saving}
        editing={editing}
        errors={errors}
      />

      <NestedProcedureSheet
        open={sheetOpen}
        values={editingDraft?.values ?? null}
        onSelectProcedure={selectProcedure}
        onChange={(next) => {
          setProcedures((current) =>
            current.map((draft) =>
              draft.draftId === editingDraftId
                ? markOverridden({ ...draft, values: next }, draft.values)
                : draft,
            ),
          );
        }}
        onDone={() => {
          setSheetOpen(false);
          setEditingDraftId(null);
        }}
        onRemove={removeProcedure}
        onClose={() => {
          setSheetOpen(false);
          setEditingDraftId(null);
        }}
        recentKeys={recentKeys}
        errors={{}}
      />

      <Dialog
        open={inheritOffer !== null}
        onClose={() => setInheritOffer(null)}
        title="Apply to the procedures too?"
        description={`You changed this case's date, age band or capability level. The ${String(storedChildCount)} procedure${storedChildCount === 1 ? '' : 's'} already saved inside it can keep what they have, or take the new values.`}
        footer={
          <>
            <Button
              onClick={() => {
                setInheritOffer(null);
                void save(false, duplicateAfter);
              }}
            >
              Keep as they are
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setInheritOffer(null);
                void save(true, duplicateAfter);
              }}
            >
              Apply to all
            </Button>
          </>
        }
      />

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this case?"
        description={
          storedChildCount === 0
            ? 'You can recover it from Settings for 30 days.'
            : `This will also delete the ${String(storedChildCount)} procedure${storedChildCount === 1 ? '' : 's'} recorded inside it. You can recover them from Settings for 30 days.`
        }
        footer={
          <>
            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => void deleteCase()}>
              Delete
            </Button>
          </>
        }
      />
    </>
  );
}

function collect(issues: readonly { path: PropertyKey[]; message: string }[]): FormErrors {
  const collected: FormErrors = {};

  for (const issue of issues) {
    const field = issue.path[0];
    if (typeof field === 'string' && collected[field] === undefined) {
      collected[field] = issue.message;
    }
  }

  return collected;
}

/**
 * Saving and duplicating navigates to this same route with a fresh prefill.
 * Keying on the location gives that navigation a new instance, so the form
 * seeds from the copy instead of keeping what was typed into the last one.
 */
export function LogCaseRoute() {
  return <LogCaseRouteForm key={useLocation().key} />;
}
